import { isBuildingSprinklered } from "./index47.js";
import { GetAllBones, GetAllJoints, RG_TYPE, roundDecimals } from "@project-rouge/rg-core";
import { getRgObjectsOfType as getRgObjectsOfType$1, queryPivot, transformDir } from "@project-rouge/rg-three";
import { Line3, Vector3 } from "three";
var _routeDir = new Vector3();
var _altRouteDir = new Vector3();
function validateApartmentTravelDistances(building, aggConfig, log = true) {
	const residentialLevels = building.data.levels.filter((l) => !l.isPodium && !l.isRoof);
	const bones = GetAllBones(building.data.anatomy);
	const joints = GetAllJoints(building.data.anatomy);
	const checkedRecord = {};
	const travelDist = isBuildingSprinklered(building, aggConfig) ? aggConfig.maxTravelDistSprinklered : aggConfig.maxTravelDist;
	const travelDist2Ways = aggConfig.maxTravelDistBidirectional;
	const flats = getRgObjectsOfType$1(building, RG_TYPE.Apartment);
	const cores = getRgObjectsOfType$1(building, RG_TYPE.Core);
	const entrances = getRgObjectsOfType$1(building, RG_TYPE.Entrance);
	const cornerNodes = joints.map(getCornerNodes);
	for (const level of residentialLevels) {
		const levelKey = getLevelKey(building, level.levelIndex);
		if (checkedRecord[levelKey]) continue;
		for (const cornerNode of cornerNodes) cornerNode.link = [];
		const levelFlat = flats.filter((a) => a.data.level === level.levelIndex);
		const levelCores = cores.filter((c) => c.data.level === level.levelIndex);
		const levelEntrances = entrances.filter((e) => e.data.level === level.levelIndex);
		const flatNodes = levelFlat.map((obj) => createNodes(obj, bones)).flat();
		const entranceNodes = levelEntrances.map((obj) => createNodes(obj, bones)).flat();
		const coreNodes = levelCores.map((c) => createNodes(c, bones)).flat();
		linkNodes([
			...flatNodes,
			...entranceNodes,
			...coreNodes,
			...cornerNodes
		]);
		for (const flat of flatNodes) {
			let isValid = true;
			const route = getClosestExit(flat);
			const dist = getNodesDist(route);
			if (dist <= travelDist) continue;
			if (dist > travelDist2Ways) {
				log && console.log(`${flat.obj.name} closest exit is at ${roundDecimals(dist)}m. Expected ${travelDist} single or ${travelDist2Ways} double.`);
				isValid = false;
				break;
			}
			const usedPaths = [...route];
			const usedEnds = [route[route.length - 1]];
			while (true) {
				const altRoute = getClosestExit(flat, usedEnds);
				if (altRoute.length < 2) {
					log && console.log(`${flat.obj.name} has only 1 exit at ${roundDecimals(dist)}m. Expected ${travelDist} single or ${travelDist2Ways} double.`);
					isValid = false;
					break;
				}
				usedEnds.push(altRoute[altRoute.length - 1]);
				usedPaths.push(...altRoute);
				const routeDir = getNodesDir(route[0], route[1], _routeDir);
				const altRouteDir = getNodesDir(altRoute[0], altRoute[1], _altRouteDir);
				if (roundDecimals(routeDir.dot(altRouteDir)) !== 1) break;
				if (getNodesDist(altRoute.slice(0, 2)) <= travelDist) break;
			}
			if (isValid) continue;
			return false;
		}
		checkedRecord[levelKey] = true;
	}
	return true;
}
function getLevelKey(building, levelId) {
	return building.children.filter((c) => [RG_TYPE.Apartment, RG_TYPE.Core].includes(c.type) && c.data.level === levelId).map((a) => `${a.name},${roundDecimals(a.pos[0])},${roundDecimals(a.pos[2])}`).join(",");
}
var ObjNodeType = /* @__PURE__ */ function(ObjNodeType$1) {
	ObjNodeType$1["apartment"] = "apartment";
	ObjNodeType$1["core"] = "core";
	ObjNodeType$1["entrance"] = "entrance";
	ObjNodeType$1["corner"] = "corner";
	return ObjNodeType$1;
}(ObjNodeType || {});
var NodeTypeDictionary = {
	[RG_TYPE.Apartment]: ObjNodeType.apartment,
	[RG_TYPE.Core]: ObjNodeType.core,
	[RG_TYPE.Entrance]: ObjNodeType.entrance
};
var _line3 = new Line3();
var _point = new Vector3();
var pivot = [
	.5,
	0,
	.5
];
var _forward = new Vector3();
var _pointDir = new Vector3();
function createNodes(obj, bones) {
	const boneList = bones.filter((b) => b.muscles.some((m) => m.id === obj.data.muscleID));
	if (obj.type === RG_TYPE.Core) boneList.push(...bones.filter((b) => b.joints.some((j) => j.id === obj.data.jointID)));
	if (obj.type === RG_TYPE.Core) queryPivot({
		obj,
		pivot,
		type: RG_TYPE.Core,
		out: _point
	});
	else queryPivot({
		obj,
		pivot,
		type: RG_TYPE.Door,
		out: _point,
		query: (d) => !!d.data.isEntrance
	});
	const nodes = [];
	let width = 0;
	if (obj.type === RG_TYPE.Apartment || obj.type === RG_TYPE.Entrance) width = getRgObjectsOfType$1(obj, RG_TYPE.Door).filter((d) => d.data.isEntrance)[0].size[0];
	_point.y = 0;
	for (const bone of boneList) {
		const pos = new Vector3();
		_line3.start.fromArray(bone.joints[0].pos).setY(0);
		_line3.end.fromArray(bone.joints[1].pos).setY(0);
		_line3.closestPointToPoint(_point, false, pos).setY(0);
		if (obj.type === RG_TYPE.Core) {
			transformDir(obj, _forward.set(0, 0, -1));
			if (Math.abs(roundDecimals(_forward.dot(_pointDir.subVectors(pos, _point).normalize()))) === 1) width = obj.size[0];
			else width = obj.size[2];
		}
		const objNode = {
			pos,
			obj,
			type: NodeTypeDictionary[obj.type],
			link: [],
			boneIds: [bone.id],
			width
		};
		nodes.push(objNode);
	}
	return nodes;
}
function getCornerNodes(joint) {
	return {
		pos: new Vector3().fromArray(joint.pos),
		type: ObjNodeType.corner,
		link: [],
		boneIds: joint.bones.map((b) => b.id),
		width: 0
	};
}
function linkNodes(nodes) {
	const boneNodeMap = /* @__PURE__ */ new Map();
	for (const objNode of nodes) {
		if (objNode.type === ObjNodeType.apartment) continue;
		for (const bone of objNode.boneIds) {
			if (!boneNodeMap.has(bone)) boneNodeMap.set(bone, []);
			boneNodeMap.get(bone).push(objNode);
		}
	}
	for (const objNode of nodes) {
		const linkID = objNode.boneIds.slice().sort().join("|");
		if (!boneNodeMap.has(linkID)) boneNodeMap.set(linkID, objNode.boneIds.map((b) => boneNodeMap.get(b)).flat());
		objNode.link = boneNodeMap.get(linkID);
	}
}
var exitNodeTypes = [
	ObjNodeType.core,
	ObjNodeType.entrance,
	ObjNodeType.corner
];
function getClosestExit(objNode, skipNodes = []) {
	let dist = Infinity;
	let escapeRoute = [objNode];
	for (const link of objNode.link) {
		if (link === objNode) continue;
		if (skipNodes.includes(link)) continue;
		if (link.obj && skipNodes.some((s) => s.obj === link.obj)) continue;
		if (!exitNodeTypes.includes(link.type)) continue;
		const newDist = getNodesDist([objNode, link]);
		if (newDist > dist) continue;
		if (link.type === ObjNodeType.corner) {
			const nextNodes = getClosestExit(link, [objNode, ...skipNodes]);
			const dist2 = getNodesDist([objNode, ...nextNodes]);
			if (dist2 < dist) {
				dist = dist2;
				escapeRoute = [objNode, ...nextNodes];
			}
		} else {
			dist = newDist;
			escapeRoute = [objNode, link];
		}
	}
	return escapeRoute;
}
function getNodesDir(from, to, out = new Vector3()) {
	return out.subVectors(to.pos, from.pos).normalize();
}
function getNodesDist(nodes) {
	let dist = 0;
	for (let i = 1; i < nodes.length; i++) {
		const a = nodes[i - 1];
		const b = nodes[i];
		dist += a.pos.distanceTo(b.pos) - (a.width + b.width) * .5;
	}
	return dist;
}
export { validateApartmentTravelDistances };

//# sourceMappingURL=index185.js.map